import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
    Search,
    X,
    User,
    UserCircle,
    Building,
    Phone,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';

const ROLE_NAMES = {
    admin_full: 'Admin Full',
    admin: 'Admin',
    comptable: 'Comptable',
    vp: 'VP',
    apporteur: 'Apporteur'
};

// Composant de recherche universelle (réutilisable bureau + mobile)
export const GlobalSearch = ({ showCallButton = false, autoFocus = false, variant = 'desktop', onResultNavigate }) => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState({ prospects: [], users: [], teams: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const debounceRef = useRef(null);

    const isMobile = variant === 'mobile';

    const searchGlobal = useCallback(async (searchQuery) => {
        if (!searchQuery || searchQuery.length < 2) {
            setResults({ prospects: [], users: [], teams: [] });
            return;
        }

        setIsLoading(true);
        try {
            const response = await api.get('/search/global', { params: { q: searchQuery, limit: 20 } });
            setResults(response.data);
            setShowDropdown(true);
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            searchGlobal(query);
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [query, searchGlobal]);

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                inputRef.current && !inputRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (item) => {
        setQuery('');
        setShowDropdown(false);
        // Sur mobile : navigation directe (l'overlay se ferme), pas de modale
        // (la modale s'afficherait derrière l'overlay plein écran).
        if (isMobile) {
            if (item.type === 'prospect') navigate(`/prospects/${item.id}`);
            else if (item.type === 'team') navigate('/teams');
            else if (item.type === 'user') navigate(`/dashboard/team/user/${item.id}`);
            if (onResultNavigate) onResultNavigate();
            return;
        }
        setSelectedItem(item);
        setShowDetailModal(true);
    };

    const hasResults = results.prospects.length > 0 || results.users.length > 0 || results.teams.length > 0;

    return (
        <div className={cn("relative", isMobile ? "w-full" : "flex-1 max-w-md")}>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => query.length >= 2 && setShowDropdown(true)}
                    placeholder="Rechercher prospects, utilisateurs, équipes..."
                    className="w-full pl-10 pr-10 py-2.5 bg-slate-100 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500"
                    data-testid="universal-search-input"
                />
                {query && (
                    <button
                        onClick={() => { setQuery(''); setShowDropdown(false); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        data-testid="search-clear-btn"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {showDropdown && query.length >= 2 && (
                <div
                    ref={dropdownRef}
                    className={cn(
                        "absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-y-auto",
                        isMobile ? "max-h-[70vh]" : "max-h-96"
                    )}
                    data-testid="search-results-dropdown"
                >
                    {isLoading ? (
                        <div className="p-4 text-center text-slate-500">
                            <div className="w-5 h-5 border-2 border-slate-200 border-t-yellow-500 rounded-full animate-spin mx-auto" />
                        </div>
                    ) : !hasResults ? (
                        <div className="p-4 text-center text-slate-500 text-sm">
                            Aucun résultat pour "{query}"
                        </div>
                    ) : (
                        <div className="py-2">
                            {results.prospects.length > 0 && (
                                <div>
                                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase bg-slate-50">
                                        Prospects ({results.prospects.length})
                                    </div>
                                    {results.prospects.map((p) => (
                                        <div
                                            key={p.id}
                                            className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-50"
                                        >
                                            <button
                                                onClick={() => handleSelect(p)}
                                                className="flex items-center gap-3 flex-1 min-w-0 text-left"
                                                data-testid={`search-result-prospect-${p.id}`}
                                            >
                                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                                    <User className="w-4 h-4 text-blue-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-slate-900 truncate">
                                                        {p.first_name} {p.last_name}
                                                    </p>
                                                    <p className="text-xs text-slate-500 truncate">
                                                        {p.company_name || p.city || 'Prospect'}
                                                    </p>
                                                </div>
                                                {p.prospect_class && (
                                                    <span className={cn(
                                                        "px-2 py-0.5 text-xs font-medium rounded flex-shrink-0",
                                                        p.prospect_class === 'premium' && "bg-yellow-100 text-yellow-700",
                                                        p.prospect_class === 'standard' && "bg-slate-100 text-slate-700"
                                                    )}>
                                                        {p.prospect_class}
                                                    </span>
                                                )}
                                            </button>
                                            {showCallButton && p.phone && (
                                                <a
                                                    href={`tel:${p.phone}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="flex-shrink-0 w-9 h-9 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center text-white transition-colors"
                                                    aria-label={`Appeler ${p.first_name} ${p.last_name}`}
                                                    data-testid={`search-call-prospect-${p.id}`}
                                                >
                                                    <Phone className="w-4 h-4" />
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {results.users.length > 0 && (
                                <div>
                                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase bg-slate-50">
                                        Utilisateurs ({results.users.length})
                                    </div>
                                    {results.users.map((u) => (
                                        <button
                                            key={u.id}
                                            onClick={() => handleSelect(u)}
                                            className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-50 text-left"
                                            data-testid={`search-result-user-${u.id}`}
                                        >
                                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                                <UserCircle className="w-4 h-4 text-green-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-slate-900 truncate">
                                                    {u.first_name} {u.last_name}
                                                </p>
                                                <p className="text-xs text-slate-500 truncate">
                                                    {ROLE_NAMES[u.role] || u.role} {u.team?.name ? `• ${u.team.name}` : ''}
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {results.teams.length > 0 && (
                                <div>
                                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase bg-slate-50">
                                        Équipes ({results.teams.length})
                                    </div>
                                    {results.teams.map((t) => (
                                        <button
                                            key={t.id}
                                            onClick={() => handleSelect(t)}
                                            className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-50 text-left"
                                            data-testid={`search-result-team-${t.id}`}
                                        >
                                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                                <Building className="w-4 h-4 text-purple-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-slate-900 truncate">
                                                    {t.name}
                                                </p>
                                                <p className="text-xs text-slate-500 truncate">
                                                    {t.member_count} membres
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <DetailModal
                item={selectedItem}
                open={showDetailModal}
                onClose={() => setShowDetailModal(false)}
                onNavigate={onResultNavigate}
                showCallButton={showCallButton}
            />
        </div>
    );
};

// Modal de détails d'un élément sélectionné
const DetailModal = ({ item, open, onClose, onNavigate, showCallButton = false }) => {
    const navigate = useNavigate();

    if (!item) return null;

    const handleViewDetails = () => {
        if (item.type === 'prospect') {
            navigate(`/prospects/${item.id}`);
        } else if (item.type === 'team') {
            navigate(`/teams`);
        }
        onClose();
        if (onNavigate) onNavigate();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {item.type === 'prospect' && <User className="w-5 h-5 text-blue-600" />}
                        {item.type === 'user' && <UserCircle className="w-5 h-5 text-green-600" />}
                        {item.type === 'team' && <Building className="w-5 h-5 text-purple-600" />}
                        {item.type === 'prospect' && 'Prospect'}
                        {item.type === 'user' && 'Utilisateur'}
                        {item.type === 'team' && 'Équipe'}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Détails de l'élément sélectionné
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {item.type === 'prospect' && (
                        <>
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                                    <User className="w-6 h-6 text-blue-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg">{item.first_name} {item.last_name}</h3>
                                    {item.company_name && <p className="text-sm text-slate-500">{item.company_name}</p>}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                {item.phone && (
                                    <div>
                                        <span className="text-slate-500">Téléphone:</span>
                                        <p className="font-medium">{item.phone}</p>
                                    </div>
                                )}
                                {item.city && (
                                    <div>
                                        <span className="text-slate-500">Ville:</span>
                                        <p className="font-medium">{item.city}</p>
                                    </div>
                                )}
                                {item.prospect_class && (
                                    <div>
                                        <span className="text-slate-500">Classe:</span>
                                        <p className="font-medium capitalize">{item.prospect_class}</p>
                                    </div>
                                )}
                            </div>
                            {showCallButton && item.phone && (
                                <a
                                    href={`tel:${item.phone}`}
                                    className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                                    data-testid="call-prospect-btn"
                                >
                                    <Phone className="w-4 h-4" />
                                    Appeler
                                </a>
                            )}
                            <button
                                onClick={handleViewDetails}
                                className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                data-testid="view-prospect-details-btn"
                            >
                                Voir le dossier complet
                            </button>
                        </>
                    )}

                    {item.type === 'user' && (
                        <>
                            <div className="flex items-center gap-4">
                                {item.profile_picture ? (
                                    <img src={item.profile_picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                                ) : (
                                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                                        <UserCircle className="w-6 h-6 text-green-600" />
                                    </div>
                                )}
                                <div>
                                    <h3 className="font-semibold text-lg">{item.first_name} {item.last_name}</h3>
                                    <p className="text-sm text-slate-500">{ROLE_NAMES[item.role] || item.role}</p>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm">
                                {item.email && (
                                    <div>
                                        <span className="text-slate-500">Email:</span>
                                        <p className="font-medium">{item.email}</p>
                                    </div>
                                )}
                                {item.phone && (
                                    <div>
                                        <span className="text-slate-500">Téléphone:</span>
                                        <p className="font-medium">{item.phone}</p>
                                    </div>
                                )}
                                {item.vp_name && (
                                    <div>
                                        <span className="text-slate-500">VP:</span>
                                        <p className="font-medium">{item.vp_name}</p>
                                    </div>
                                )}
                                {item.team && (
                                    <div>
                                        <span className="text-slate-500">Équipe:</span>
                                        <p className="font-medium">{item.team.name}</p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {item.type === 'team' && (
                        <>
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                                    <Building className="w-6 h-6 text-purple-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg">{item.name}</h3>
                                    <p className="text-sm text-slate-500">{item.member_count} membres</p>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm">
                                {item.vp_payeur_name && (
                                    <div>
                                        <span className="text-slate-500">VP Payeur:</span>
                                        <p className="font-medium">{item.vp_payeur_name}</p>
                                    </div>
                                )}
                                {item.vp_superviseur_name && (
                                    <div>
                                        <span className="text-slate-500">VP Superviseur:</span>
                                        <p className="font-medium">{item.vp_superviseur_name}</p>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={handleViewDetails}
                                className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                                data-testid="view-team-details-btn"
                            >
                                Voir les équipes
                            </button>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default GlobalSearch;
